// Pure adapters between the admin Card Editor's form state and every
// shape the rest of the app deals in: the on-disk `card.json`, the
// AI-Fill prefill payload, and a `CardView` for the preview pane.
//
// Living here (rather than buried in `pages/CardEditorPage.tsx`) gets
// us three things: the page's UI body shrinks to layout-only, every
// adapter is unit-testable in isolation, and future surfaces that
// want a "duplicate this card" / "import from clipboard" flow can
// reuse `detailToForm` / `formToCardJson` directly.

import type { AdminCardDetail, AdminPool, CardEditorPrefilled } from "../api/client";
import type { CardView } from "../api/types";

export const CARD_TYPES = ["creature", "spell", "enchantment", "trap"] as const;
export const FACTIONS = ["humans", "goblins", "dwarves", "orcs", "elves"] as const;
export const RARITIES = ["common", "uncommon", "rare", "legendary"] as const;

// Sentinel: empty string means "let AI Fill choose at random" for
// type / faction / rarity. The selects in the editor expose this as
// an "ANY" option at the top of each dropdown. The Save path rejects
// empty values (a card on disk needs concrete axes); AI Fill omits
// empty axes from the prefilled card_json so the LLM rolls them.
export const ANY_VALUE = "" as const;

export type CardType = (typeof CARD_TYPES)[number];

export interface CardFormState {
  id: string;
  name: string;
  // Empty string = ANY: AI Fill rolls this axis randomly. Non-empty
  // values must be a member of CARD_TYPES.
  type: CardType | "";
  cost: string;
  attack: string;
  life: string;
  // Empty string = ANY (rolled by AI Fill). Otherwise a FACTIONS member.
  faction: string;
  // Empty string = ANY (rolled by AI Fill). Otherwise a RARITIES member.
  rarity: string;
  // Filesystem location: `base_set` / `main` / `npc_cards` / `gen` (the
  // dir under `card-engine/cards/`). Changing this in edit mode triggers
  // a /move call on save. Distinct from `expansion_set`, which is purely
  // thematic metadata persisted into card.json. The two were one field
  // pre-rename and the editor's "Set" dropdown still gates both — keep
  // them apart here so detailToForm can stop falling back the wrong way.
  pool: string;
  expansion_set: string;
  // Creature subtypes, both optional and creature-only. Empty string =
  // none. Members of RACES / CLASSES from data/subtypes.json. Persisted
  // to card.json only when the card is a creature and the value is set.
  race: string;
  class: string;
  rules_text: string;
  flavor: string;
  image_generation_prompt: string;
  script: string;
  // AI Fill spec axes — not persisted to card.json. Empty means "let
  // the sampler pick"; non-empty pins the value for the next AI Fill.
  seed_words: string;
  typicality: string;
  // Free-form notes the admin wants the generator to consider on the
  // next AI Fill — anything the structured axes can't express ("lean
  // gross, not heroic", "should reference Tordrin's quest"). Passed
  // through verbatim into the user prompt; empty string omits it.
  notes: string;
}

export const BLANK_CARD_FORM: CardFormState = {
  id: "",
  name: "",
  // ANY by default — a fresh card-creation form rolls everything
  // randomly when AI Fill is clicked. The user can pin any axis by
  // picking a concrete value from the dropdown.
  type: "",
  cost: "",
  attack: "",
  life: "",
  faction: "",
  rarity: "",
  pool: "main",
  expansion_set: "high_fantasia",
  race: "",
  class: "",
  rules_text: "",
  flavor: "",
  image_generation_prompt: "",
  script: "",
  seed_words: "",
  typicality: "",
  notes: "",
};

/** Decode an `AdminCardDetail` (the admin GET shape) into a form. The
 *  raw JSON-text is parsed best-effort; a parse failure falls back to
 *  detail-level fields so the user can still see the editor open and
 *  fix the malformed JSON manually. */
export function detailToForm(detail: AdminCardDetail): CardFormState {
  let parsed: Record<string, unknown> = {};
  try {
    parsed = JSON.parse(detail.json_text) as Record<string, unknown>;
  } catch {
    // Surface the raw json so the user can fix it manually — the
    // form fields below will be populated from `detail` as best we can.
  }
  const get = (k: string) => parsed[k];
  return {
    id: typeof get("id") === "string" ? (get("id") as string) : detail.id,
    name: (get("name") as string) ?? "",
    type: ((get("type") as CardType) ?? "creature") as CardType,
    cost: numStr(get("cost")),
    attack: numStr(get("attack")),
    life: numStr(get("life")),
    faction: (get("faction") as string) ?? "humans",
    rarity: (get("rarity") as string) ?? "common",
    pool: detail.pool,
    // `expansion_set` is content-pack metadata. Cards from before the
    // rename only have the legacy `world` key; honor that as fallback,
    // then default to the one set we ship today.
    expansion_set:
      (get("expansion_set") as string) ??
      (get("world") as string) ??
      "high_fantasia",
    // Creature subtypes. Read them back so editing + saving a card no
    // longer silently drops its race/class (the round-trip used to omit
    // them entirely, stripping subtypes off every saved creature).
    race: (get("race") as string) ?? "",
    class: (get("class") as string) ?? "",
    rules_text: (get("rules_text") as string) ?? "",
    flavor: (get("flavor") as string) ?? "",
    image_generation_prompt: (get("image_generation_prompt") as string) ?? "",
    script: detail.lua_text ?? "",
    seed_words: "",
    typicality: "",
    notes: "",
  };
}

/** Encode the form into the dict that goes into `card.json` on disk.
 *  Empty optional strings are dropped (no `flavor: ""` in the saved
 *  file); stats are coerced to integers; the script field is set to
 *  the literal `"card.lua"` filename when the form has lua content. */
export function formToCardJson(form: CardFormState): Record<string, unknown> {
  const json: Record<string, unknown> = {
    id: form.id.trim(),
    name: form.name.trim(),
    type: form.type,
    cost: parseIntOr(form.cost, 0),
    attack: parseIntOr(form.attack, 0),
    life: parseIntOr(form.life, 0),
    faction: form.faction,
    expansion_set: form.expansion_set,
    rarity: form.rarity,
  };
  // Subtypes are creature-only per the card schema; never write them
  // onto a spell/enchantment/trap even if the form still holds a value
  // from before the type was switched.
  if (form.type === "creature" && form.race.trim()) json.race = form.race;
  if (form.type === "creature" && form.class.trim()) json.class = form.class;
  if (form.rules_text.trim()) json.rules_text = form.rules_text;
  if (form.flavor.trim()) json.flavor = form.flavor;
  if (form.image_generation_prompt.trim()) {
    json.image_generation_prompt = form.image_generation_prompt;
  }
  if (form.script.trim()) json.script = "card.lua";
  return json;
}

/** Encode the form into the AI-Fill prefill payload. Empty / zero
 *  fields are omitted from `card_json` so the LLM treats them as
 *  "fill these in" rather than locked-to-blank. The spec block is
 *  only emitted when the admin pinned at least one axis — a missing
 *  block tells the Python tool to fall back to random sampling. */
export function formToPrefilled(form: CardFormState): CardEditorPrefilled {
  // Empty axes are ANY — omit them from card_json so the Python tool's
  // prefill merge doesn't pin them, and the sampler rolls. Concrete
  // values get sent through and become locked-in (per the
  // "admin-supplied prefilled fields are sacred" rule).
  const card_json: Record<string, unknown> = {
    expansion_set: form.expansion_set,
    // `pool` routes the AI-Fill output dir (`cards/<pool>/<id>/`).
    // Distinct from `expansion_set`, which is content-pack metadata
    // (used for the `--set` flag, not for picking a destination dir).
    pool: form.pool,
  };
  if (form.type !== "") card_json.type = form.type;
  if (form.faction !== "") card_json.faction = form.faction;
  // Pinned subtypes are sacred too — pass them through so AI Fill keeps
  // them. Creature-only, matching the editor's UI gating.
  if (form.type === "creature" && form.race.trim()) card_json.race = form.race;
  if (form.type === "creature" && form.class.trim()) card_json.class = form.class;
  if (form.rarity !== "") card_json.rarity = form.rarity;
  if (form.id.trim()) card_json.id = form.id.trim();
  if (form.name.trim()) card_json.name = form.name.trim();
  if (form.cost !== "") card_json.cost = parseIntOr(form.cost, 0);
  if (form.attack !== "") card_json.attack = parseIntOr(form.attack, 0);
  if (form.life !== "") card_json.life = parseIntOr(form.life, 0);
  if (form.rules_text.trim()) card_json.rules_text = form.rules_text;
  if (form.flavor.trim()) card_json.flavor = form.flavor;
  if (form.image_generation_prompt.trim()) {
    card_json.image_generation_prompt = form.image_generation_prompt;
  }

  const spec: NonNullable<CardEditorPrefilled["spec"]> = {};
  const seedWords = parseSeedWords(form.seed_words);
  if (seedWords.length > 0) spec.seed_words = seedWords;
  const typ = parseTypicality(form.typicality);
  if (typ != null) spec.typicality = typ;
  const notes = form.notes.trim();
  if (notes) spec.notes = notes;

  return {
    card_json,
    script: form.script.trim() ? form.script : null,
    ...(Object.keys(spec).length > 0 ? { spec } : {}),
  };
}

/** Synthesize a `CardView` for the live preview. `originalId`
 *  drives the image URL: while a card is brand-new (no save yet) the
 *  preview falls back to the sigil placeholder. `imageVersion`
 *  cache-busts after a regen-image job completes. */
export function formToCardView(
  form: CardFormState,
  originalId: string | null,
  pool: AdminPool,
  imageVersion: number,
): CardView {
  const atk = parseIntOr(form.attack, 0);
  const life = parseIntOr(form.life, 0);
  const baseImageUrl = originalId ? `/card-images/${originalId}` : null;
  const image_url =
    baseImageUrl && imageVersion > 0
      ? `${baseImageUrl}?v=${imageVersion}`
      : baseImageUrl;
  return {
    entity_id: 0,
    def_id: form.id || "new_card",
    name: form.name || "Untitled card",
    // The live preview falls back to "creature" while the admin
    // hasn't picked a type — the snapshot only renders one frame, so
    // we need a concrete type for layout. Doesn't affect what's saved
    // or sent to AI Fill (those paths look at form.type directly).
    type: form.type === "" ? "creature" : form.type,
    cost: parseIntOr(form.cost, 0),
    base_attack: atk,
    base_life: life,
    current_attack: atk,
    current_life: life,
    damage_marked: 0,
    remaining_life: life,
    can_attack: false,
    is_attacking: false,
    has_script: form.script.trim().length > 0,
    flavor: form.flavor || null,
    rules_text: form.rules_text || null,
    image_url,
    // Same reason as the `type` fallback above: the preview needs a
    // concrete faction for layout. Doesn't affect what's saved.
    faction: form.faction === "" ? "humans" : form.faction,
    // Subtypes only read on the type line for creatures; null otherwise
    // so switching type clears them from the preview.
    race: form.type === "creature" && form.race ? form.race : null,
    class: form.type === "creature" && form.class ? form.class : null,
    expansion_set: form.expansion_set || pool,
    rarity: form.rarity,
    summon_only: false,
    seal_counters: 0,
    face_down: false,
    keywords: [],
  };
}

/** Split a comma- or whitespace-separated string into clean tokens.
 *  Returns `[]` when the input is blank or only separators. */
export function parseSeedWords(raw: string): string[] {
  return raw
    .split(/[,\s]+/)
    .map((w) => w.trim())
    .filter((w) => w.length > 0);
}

/** Parse a typicality string into 1..10, or `null` for "let the
 *  sampler pick." Out-of-range / non-integer / blank all return null. */
export function parseTypicality(raw: string): number | null {
  if (raw === "") return null;
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n)) return null;
  if (n < 1 || n > 10) return null;
  return n;
}

function parseIntOr(s: string, fallback: number): number {
  const n = Number.parseInt(s, 10);
  return Number.isFinite(n) ? n : fallback;
}

function numStr(v: unknown): string {
  return typeof v === "number" ? String(v) : typeof v === "string" ? v : "";
}
